export const PROFILE = {
  name: "Adlan Muammar Shiddiq",
  handle: "ADLAN MS PORTOFOLIO",
  tagline: "AI / ML Engineer",
  subTagline: "Computer Vision · LLM / RAG · Software Engineering",
  summary:
    "AI Engineer specializing in Machine Learning, Computer Vision, and Generative AI architectures (RAG & Agentic workflows). Combines a rigorous background in Data Science with hands-on software engineering to design, train, and scale intelligent software solutions — translating complex analytical patterns into robust products that automate operations and enhance enterprise decision-making.",
  location: "Bandung, Indonesia",
  email: "adlanmuammar9@gmail.com",
  phone: "+62 819-4991-2144",
  linkedinUrl: "https://www.linkedin.com/in/adlan-muammar-s-115301247/",
  cvUrl: "/AdlanMuammarShiddiq_CV.pdf",
};

export const EXPERIENCE = [
  {
    id: "ammegatech",
    company: "PT AM Megatek Global Innovasi",
    shortName: "AMMEGATECH",
    role: "IoT & AI Engineer",
    period: "Feb 2026 — July 2026",
    floor: "02",
    bullets: [
      "Developed an AI Agentic CRM product enabling autonomous customer interaction workflows and intelligent pipeline management using LLM orchestration and RAG architecture.",
      "Built Ammegavision, a computer vision system leveraging TensorRT for real-time object detection, tracking, and visual analytics tailored to enterprise use cases.",
    ],
  },
  {
    id: "magnet",
    company: "PT Mitra Akses Globalindo",
    shortName: "MAGNET",
    role: "Research & Development, AI & IoT",
    period: "Feb 2024 — Jan 2026",
    floor: "01",
    bullets: [
      "Analyzed customer data to identify business opportunities and developed strategic roadmaps, including E-Nose technology implementation aligned with the business plan.",
      "Designed and presented data-driven insights using BI dashboards to support stakeholder decision-making.",
      "Built deep learning models (YOLO, PyTorch, TensorFlow) for real-time Driver Monitoring Systems detecting drowsiness, distraction, and gaze direction on Raspberry Pi infrastructure.",
      "Architected LLM-based systems for intelligent analytics and automated decision-support, enabling natural language interaction and AI-driven business insights.",
    ],
  },
];

export const PROJECTS = [
  {
    id: "vision-hub",
    index: "01",
    title: "Vision Hub",
    tag: "Computer Vision",
    description:
      "Enterprise computer vision system (Ammegavision) for real-time object detection, tracking, and visual analytics across live CCTV feeds.",
    longDescription:
      "Built as the flagship product at AMMEGATECH — a TensorRT-accelerated pipeline processing multi-camera video streams for real-time object detection and tracking, delivering visual analytics tailored for enterprise security and operations use cases.",
    stack: ["TensorRT", "Computer Vision", "GPU Inference", "Python"],
    role: "Built",
    context: "PT AM Megatek Global Innovasi",
  },
  {
    id: "people-analytics",
    index: "02",
    title: "People Analytics",
    tag: "Business Intelligence",
    description:
      "Customer behavior & segmentation dashboards translating raw transaction data into churn, retention and market-segment insight.",
    longDescription:
      "A suite of BI dashboards analyzing customer/passenger behaviour — churn rate, retention and segmentation by industry, province and service — used to support strategic stakeholder decisions.",
    stack: ["Power BI", "Tableau", "Looker Studio", "SQL"],
    role: "Designed & Presented",
    context: "PT Mitra Akses Globalindo",
  },
  {
    id: "ai-detection-system",
    index: "03",
    title: "AI Detection System",
    tag: "Deep Learning",
    description:
      "Real-time Driver Monitoring System detecting drowsiness, distraction and gaze direction, deployed on edge hardware.",
    longDescription:
      "A YOLO + OpenCV based deep learning system trained with PyTorch/TensorFlow to monitor driver state in real time, integrated with Raspberry Pi infrastructure for on-device inference.",
    stack: ["YOLO", "OpenCV", "PyTorch", "TensorFlow", "Raspberry Pi"],
    role: "Built",
    context: "PT Mitra Akses Globalindo",
  },
  {
    id: "edge-ai-pipeline",
    index: "04",
    title: "Edge AI Pipeline",
    tag: "IoT · MLOps",
    description:
      "Real-time ETL streaming pipeline for IoT sensor data with a predictive early-warning model at 83% production accuracy.",
    longDescription:
      "Smart Environment & Early Warning System — a real-time ETL streaming pipeline ingesting ESP32 sensor telemetry, paired with a logistic regression model predicting environmental metrics for early-warning detection.",
    stack: ["ETL Streaming", "Machine Learning", "ESP32", "IoT"],
    role: "Built & Developed",
    context: "Independent / R&D Project — Nov 2025",
  },
  {
    id: "axiomai",
    index: "05",
    title: "AxiomAI",
    tag: "GenAI · RAG",
    description:
      "RAG-based AI assistant for data analysts — automates query handling, interpretation and result visualization.",
    longDescription:
      "A Retrieval-Augmented Generation system built to automate data-analysis workflows, letting analysts ask natural-language questions and receive grounded insights, interpretation, and visualizations.",
    stack: ["RAG", "LangChain", "LLM", "Python"],
    role: "Developed",
    context: "Independent Project — Sep 2025",
  },
  {
    id: "salespilot-ai",
    index: "06",
    title: "SalesPilot AI",
    tag: "Agentic AI",
    description:
      "Autonomous AI Agentic CRM orchestrating customer interaction workflows and intelligent pipeline prioritization.",
    longDescription:
      "An agentic CRM platform using LLM orchestration and RAG architecture to autonomously manage customer conversations, classify intent, retrieve context, and prioritize high-value pipeline opportunities.",
    stack: ["LLM Orchestration", "RAG", "Agentic Workflows"],
    role: "Developed",
    context: "PT AM Megatek Global Innovasi",
  },
  {
    id: "transit-pulse",
    index: "07",
    title: "Transit Pulse",
    tag: "Data Analysis",
    description:
      "Urban mobility & passenger segmentation analysis across Transjakarta corridors, informing market-segment recommendations.",
    longDescription:
      "Analyzed card-usage patterns across bus stops and corridors to deliver market segmentation recommendations, identifying target passenger segments based on data-driven criteria.",
    stack: ["Data Analysis", "SQL", "BI Dashboards"],
    role: "Analyzed",
    context: "Independent Project — Oct 2024",
  },
];

export const SKILLS = {
  categories: [
    {
      name: "Machine Learning & AI",
      items: ["Machine Learning", "Deep Learning", "Computer Vision", "NLP", "RAG & Agentic Systems", "LangChain / LangGraph"],
    },
    {
      name: "Data Engineering",
      items: ["ETL & Data Pipelines", "Vector Databases", "SQL", "Python"],
    },
    {
      name: "BI & Visualization",
      items: ["Power BI", "Tableau", "Looker Studio"],
    },
    {
      name: "Software Engineering",
      items: ["React.js", "Node.js", "MLOps & Deployment"],
    },
  ],
  marquee: [
    "PYTHON", "COMPUTER VISION", "LLM / RAG", "PYTORCH", "TENSORFLOW", "YOLO",
    "LANGCHAIN", "SQL", "MLOPS", "AGENTIC AI", "POWER BI", "TENSORRT",
  ],
};

export const EDUCATION = [
  {
    id: "cakrawala",
    institution: "Cakrawala University",
    degree: "B.Sc. Data Science (Fast-Track Employee Program)",
    detail: "GPA 3.51 · Jakarta, Indonesia",
    period: "Sep 2024 — Present",
  },
  {
    id: "purwadhika",
    institution: "Purwadhika Digital Technology School",
    degree: "Data Science & Machine Learning Program",
    detail: "Bandung, Indonesia",
    period: "Aug 2023 — Nov 2023",
  },
];

export const SECTIONS = [
  { id: "hero", label: "ADLAN MS PORTOFOLIO", number: "00", weight: 1.0 },
  { id: "about", label: "About", number: "01", weight: 1.0 },
  { id: "experience", label: "Experience", number: "02", weight: 1.3 },
  { id: "projects", label: "Projects", number: "03", weight: 1.8 },
  { id: "skills", label: "Skills", number: "04", weight: 1.2 },
  { id: "education", label: "Education", number: "05", weight: 0.8 },
  { id: "contact", label: "System Access", number: "06", weight: 1.1 },
];
